import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Typography, TextField, Skeleton, Icon, ButtonMenu } from '@ann/web-components';
import { useHomeChat } from '../../../../context';
import DeleteConversationModal from '../../../DeleteConversationModal';
import {
  Container,
  Header,
  BackButton,
  Title,
  SearchWrapper,
  GroupSection,
  GroupLabel,
  ConversationItem,
  ConversationTitleWrap,
  ItemActions,
  EditableTitleInput,
  EmptyState,
  ListWrapper,
} from './ConversationHistoryView.styled';

const RENAME_DEMO_DELAY_MS = 2000;

const getConversationId = (c) =>
  c.conversationUuid || c.conversation_uuid || c.id || c.uuid;

const getConversationDate = (c) =>
  c.last_modified || c.startedAt || c.updated_at || c.created_at || c.date || c.timestamp || null;

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const groupConversationsByDate = (conversations) => {
  const now = new Date();
  const todayStart = startOfDay(now);

  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - todayStart.getDay());

  const thirtyDaysAgo = new Date(todayStart);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const ninetyDaysAgo = new Date(todayStart);
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

  const groups = [
    { key: 'today', label: 'Today', items: [] },
    { key: 'yesterday', label: 'Yesterday', items: [] },
    { key: 'week', label: 'Earlier this week', items: [] },
    { key: 'month', label: 'Last 30 days', items: [] },
    { key: 'quarter', label: 'Last 3 months', items: [] },
  ];

  for (const conv of conversations) {
    const rawDate = getConversationDate(conv);
    const date = rawDate ? new Date(rawDate) : null;

    if (!date || isNaN(date)) {
      groups[0].items.push(conv);
      continue;
    }

    const dayStart = startOfDay(date);

    if (dayStart >= todayStart) {
      groups[0].items.push(conv);
    } else if (dayStart >= yesterdayStart) {
      groups[1].items.push(conv);
    } else if (dayStart >= weekStart) {
      groups[2].items.push(conv);
    } else if (dayStart >= thirtyDaysAgo) {
      groups[3].items.push(conv);
    } else if (dayStart >= ninetyDaysAgo) {
      groups[4].items.push(conv);
    }
  }

  return groups.filter((g) => g.items.length > 0);
};

const ConversationHistoryView = ({ onBack }) => {
  const {
    conversations,
    isLoadingConversations,
    fetchConversations,
    selectConversation,
    setConversations,
    deleteConversation,
    isDeletingConversation,
    selectedAgentId,
    defaultAgentIdForNewChat,
    selectedConversationId,
  } = useHomeChat();

  const agentIdForConversations = selectedAgentId || defaultAgentIdForNewChat;

  const [search, setSearch] = useState('');
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isUpdatingTitle, setIsUpdatingTitle] = useState(false);

  useEffect(() => {
    // Skip fetch if conversations are already loaded for the current agent (e.g. navigating from Home).
    // Re-fetch only when there's no cached data or the agent changed.
    if (conversations.length > 0) return;
    if (agentIdForConversations) {
      fetchConversations(agentIdForConversations, null);
    } else {
      fetchConversations(null, null);
    }
  }, [agentIdForConversations, fetchConversations]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = useCallback((val) => {
    const value = typeof val === 'string' ? val : val?.target?.value ?? '';
    setSearch(value);
  }, []);

  const handleSelectConversation = useCallback(
    (conversationId) => {
      selectConversation(conversationId);
      onBack?.();
    },
    [selectConversation, onBack]
  );

  const handleMenuChange = useCallback((value, conv) => {
    if (value === 'delete') {
      setConversationToDelete(conv);
    }
    if (value === 'rename') {
      setEditingConversationId(getConversationId(conv));
      setEditingTitle(conv.title || 'Untitled');
    }
  }, []);

  const handleSaveRename = useCallback(
    (id) => {
      const titleToSend = editingTitle.trim();
      if (!id || !titleToSend) {
        setEditingConversationId(null);
        setEditingTitle('');
        return;
      }
      setIsUpdatingTitle(true);
      setTimeout(() => {
        setConversations((prev) =>
          prev.map((c) =>
            getConversationId(c) === id ? { ...c, title: titleToSend } : c
          )
        );
        setEditingConversationId(null);
        setEditingTitle('');
        setIsUpdatingTitle(false);
      }, RENAME_DEMO_DELAY_MS);
    },
    [editingTitle, setConversations]
  );

  const handleCancelRename = useCallback(() => {
    setEditingConversationId(null);
    setEditingTitle('');
  }, []);

  const handleRenameKeyDown = useCallback(
    (e, id) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (editingTitle.trim()) handleSaveRename(id);
        else handleCancelRename();
      }
      if (e.key === 'Escape') handleCancelRename();
    },
    [editingTitle, handleSaveRename, handleCancelRename]
  );

  const handleCloseDeleteModal = useCallback(() => {
    setConversationToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(
    async (id) => {
      await deleteConversation(id);
      handleCloseDeleteModal();
    },
    [deleteConversation, handleCloseDeleteModal]
  );

  const filteredConversations = useMemo(() => {
    if (!search.trim()) return conversations || [];
    const term = search.toLowerCase();
    return (conversations || []).filter((c) =>
      (c.title || 'Untitled').toLowerCase().includes(term)
    );
  }, [conversations, search]);

  const groups = useMemo(
    () => groupConversationsByDate(filteredConversations),
    [filteredConversations]
  );

  return (
    <Container>
      <Header>
        <BackButton type="button" onClick={onBack} aria-label="Back to chat">
          <Icon name="arrowLeft" size="medium" color="neutral-black" />
        </BackButton>
        <Title>
          <Typography category="subtitle" size="medium" weight="bold" color="neutral-black">
            Conversation history
          </Typography>
        </Title>
      </Header>

      <SearchWrapper>
        <TextField
          iconLeft="search"
          label=""
          onChange={handleSearchChange}
          size="small"
          placeholder="Search conversations"
          value={search}
        />
      </SearchWrapper>

      <ListWrapper>
        {isLoadingConversations ? (
          <>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height="36px" width="100%" borderRadius="8px" />
            ))}
          </>
        ) : groups.length === 0 ? (
          <EmptyState>
            <Typography category="body" size="medium" weight="regular" color="body">
              {search.trim() ? 'No conversations found.' : 'No conversations yet.'}
            </Typography>
          </EmptyState>
        ) : (
          groups.map((group) => (
            <GroupSection key={group.key}>
              <GroupLabel>
                <Typography category="subtitle" size="small" weight="bold" color="neutral-black">
                  {group.label}
                </Typography>
              </GroupLabel>
              {group.items.map((conv) => {
                const convId = getConversationId(conv);
                const isActive = selectedConversationId === convId;
                const isEditing = editingConversationId === convId;

                return (
                  <ConversationItem
                    key={convId}
                    $isActive={isActive}
                    onClick={() => {
                      if (!isEditing) handleSelectConversation(convId);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isEditing) handleSelectConversation(convId);
                    }}
                  >
                    {isEditing ? (
                      isUpdatingTitle ? (
                        <Skeleton height="20px" width="100%" />
                      ) : (
                        <EditableTitleInput
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onBlur={() => handleSaveRename(convId)}
                          onKeyDown={(e) => handleRenameKeyDown(e, convId)}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                          placeholder="Chat title"
                          aria-label="Rename chat"
                        />
                      )
                    ) : (
                      <ConversationTitleWrap>
                        <Typography
                          category="body"
                          size="medium"
                          weight="regular"
                          color={isActive ? 'neutral-black' : 'body'}
                          title={conv.title || 'Untitled'}
                        >
                          {conv.title || 'Untitled'}
                        </Typography>
                      </ConversationTitleWrap>
                    )}

                    {!isEditing && (
                      <ItemActions
                        className="chat-item-actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ButtonMenu
                          alignMenu="left"
                          customIcon="dotsMenu2"
                          iconPosition="right"
                          label=""
                          menuPosition="top"
                          onChange={(value) => handleMenuChange(value, conv)}
                          size="small"
                          type="tertiary"
                          usePortal
                          width="auto"
                          disableEffects
                        >
                          <ButtonMenu.Menu icon>
                            <ButtonMenu.Item label="Rename" value="rename" icon="edit">
                              Rename
                            </ButtonMenu.Item>
                          </ButtonMenu.Menu>
                        </ButtonMenu>
                      </ItemActions>
                    )}
                  </ConversationItem>
                );
              })}
            </GroupSection>
          ))
        )}
      </ListWrapper>

      <DeleteConversationModal
        isOpen={!!conversationToDelete}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        conversationId={conversationToDelete ? getConversationId(conversationToDelete) : null}
        isLoading={isDeletingConversation}
      />
    </Container>
  );
};

export default ConversationHistoryView;
